import { FC, PropsWithChildren } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import PageLoader from 'components/Atoms/Loaders/PageLoader';
import { useAuth } from 'src/hooks/useAuth.hook';
import { AppRoutes } from 'src/pages/AppRoutes';

export interface RequireAuthProps {
  /** For strictly public routes. Defaults to `false` */
  isPublic?: boolean;
  /** Prevent isPublic toast */
  preventIsPublicToast?: boolean;
}

const RequireAuth: FC<PropsWithChildren<RequireAuthProps>> = ({
  children,
  // the functionality of the isPublic prop is quite similar to that of the useIsPublic custom hook
  // could be refactored in the future
  // if you add a route to the router with isPublic is true, make sure to add it to the publicRoutes const in useIsPublic hook as well
  isPublic = false,
  preventIsPublicToast = false,
}) => {
  const { t } = useTranslation('translation');
  const location = useLocation();

  const [auth] = useAuth();

  // When `auth` is still `undefined` the refresh is not finished and it is unknown if the user is signed in or not.
  // After the refresh round `auth` will always be an object.
  if (!auth) return <PageLoader />;

  if (!isPublic && !auth.signInCookie) {
    // Redirect them to the /sign-in page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they sign in, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate replace state={{ from: location }} to={AppRoutes.SignIn} />;
  }

  if (isPublic && auth.signInCookie) {
    // Redirect them to the home page (`/`)
    if (!preventIsPublicToast) toast.error(t('messages.error.public-route'));
    return <Navigate replace to={AppRoutes.Home} />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default RequireAuth;
